const sameValue = (a, b) => {
  if (a === b) return true
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keys = Object.keys(a)
    return keys.length === Object.keys(b).length && keys.every(key => sameValue(a[key], b[key]))
  }
  return (a == null || b == null) ? false : String(a) === String(b)
}
const optionValue = option => Object.prototype.hasOwnProperty.call(option, '_value') ? option._value : option.value

export function createNativeSelect(SearchSelect) {
  return {
    name: 'NativeSearchSelect',
    inheritAttrs: false,
    props: { value: {}, disabled: Boolean },
    data: () => ({ records: [], selectedIndex: null, inheritedDisabled: false }),
    mounted() {
      this.syncNativeOptions(); this.syncAccessibility()
      this._ariaObserver = new MutationObserver(() => this.syncAccessibility())
      this._ariaObserver.observe(this.$refs.native, { attributes: true, attributeFilter: ['aria-invalid', 'aria-describedby', 'aria-required', 'aria-errormessage', 'required'] })
      this._disabledObserver = new MutationObserver(() => this.syncDisabled())
      for (let parent = this.$refs.native.parentElement; parent; parent = parent.parentElement) if (parent.tagName === 'FIELDSET') this._disabledObserver.observe(parent, { attributes: true, attributeFilter: ['disabled'] })
    },
    updated() { this.syncNativeOptions(); this.syncAccessibility() },
    beforeDestroy() { this._ariaObserver?.disconnect(); this._disabledObserver?.disconnect() },
    computed: {
      // Compatibilidade com os refs de select existentes nas rotinas.
      options() { return this.$refs.native?.options },
      selectedOptions() { return this.$refs.native?.selectedOptions }
    },
    methods: {
      focus() { if (!this.disabled && !this.$refs.native?.matches(':disabled')) this.$refs.picker?.focus() },
      blur() { this.$refs.picker?.$refs.search?.blur() },
      syncAccessibility() {
        const native = this.$refs.native, input = this.$refs.picker?.$refs.search
        if (!native || !input) return
        for (const attr of ['aria-invalid', 'aria-describedby', 'aria-required', 'aria-errormessage']) {
          const value = native.getAttribute(attr) ?? this.$attrs[attr] ?? (attr === 'aria-required' && native.required ? 'true' : null)
          if (value == null) input.removeAttribute(attr)
          else input.setAttribute(attr, value)
        }
      },
      syncDisabled() {
        const blocked = !!this.$refs.native?.matches(':disabled')
        if (this.inheritedDisabled !== blocked) this.inheritedDisabled = blocked
        if (blocked && this.$refs.picker) this.$refs.picker.open = false
      },
      syncNativeOptions() {
        const native = this.$refs.native
        if (!native) return
        this.syncDisabled()
        const options = [...native.options]
        if (Object.prototype.hasOwnProperty.call(this.$options.propsData, 'value')) native.selectedIndex = options.findIndex(option => sameValue(optionValue(option), this.value))
        const records = options.map((option, index) => ({ id: String(index), label: option.label.trim(), disabled: option.disabled || option.parentElement?.disabled || option.hidden, __appNativeHidden: option.hidden || option.parentElement?.hidden, value: optionValue(option) }))
        if (records.length !== this.records.length || records.some((record, index) => { const old = this.records[index]; return !old || old.label !== record.label || old.disabled !== record.disabled || old.__appNativeHidden !== record.__appNativeHidden || !sameValue(old.value, record.value) })) this.records = records
        const selectedIndex = native.selectedIndex < 0 ? null : native.selectedIndex
        if (this.selectedIndex !== selectedIndex) this.selectedIndex = selectedIndex
      },
      choose(index) {
        const native = this.$refs.native
        if (this.disabled || native.matches(':disabled') || index == null || !native.options[index] || native.options[index].disabled) return
        native.selectedIndex = index
        native.dispatchEvent(new Event('input', { bubbles: true }))
        native.dispatchEvent(new Event('change', { bubbles: true }))
      },
      change(event) {
        const native = event.target
        this.selectedIndex = native.selectedIndex < 0 ? null : native.selectedIndex
        this.$emit('input', native.selectedIndex < 0 ? undefined : optionValue(native.options[native.selectedIndex]))
        this.$emit('change', event)
      }
    },
    render(h) {
      const attrs = { ...this.$attrs, disabled: this.disabled, tabindex: -1, 'aria-hidden': 'true' }
      const native = h('select', { ref: 'native', class: 'app-native-select-source', attrs, on: { change: this.change, focus: this.focus } }, this.$slots.default)
      const label = this.$attrs['aria-label'] || (this.$attrs.id && document.querySelector(`label[for="${String(this.$attrs.id).replace(/"/g, '\\"')}"]`)?.textContent.trim()) || 'Selecionar opção'
      const listeners = { ...this.$listeners }
      delete listeners.input; delete listeners.change
      const picker = h(SearchSelect, {
        ref: 'picker',
        attrs: { 'aria-label': label, 'aria-describedby': this.$attrs['aria-describedby'], 'aria-invalid': this.$attrs['aria-invalid'], 'aria-required': this.$attrs.required != null && this.$attrs.required !== false ? 'true' : undefined },
        props: { value: this.selectedIndex == null ? null : String(this.selectedIndex), options: this.records, reduce: option => option.id, label: 'label', clearable: false, disabled: this.disabled || this.inheritedDisabled, selectable: option => !option.disabled, placeholder: this.$attrs.placeholder || 'Selecione' },
        nativeOn: { keydown: event => this.$emit('keydown', event), keyup: event => this.$emit('keyup', event), focusin: event => this.$emit('focus', event), focusout: event => this.$emit('blur', event) },
        on: { input: this.choose }
      })
      return h('div', { class: 'app-native-select-host', on: Object.fromEntries(Object.entries(listeners).filter(([key]) => !['keydown', 'keyup', 'focus', 'blur'].includes(key))) }, [native, picker])
    }
  }
}
